import React, { useState } from 'react';
import { IoMdCheckmark, IoMdMic } from 'react-icons/io';

import { Dropdown, Tooltip } from 'antd';
import VoiceOverlay from 'components/common/VoiceOverlay/VoiceOverlay';
import { SpeechLang, SpeechLangOption } from 'hooks/useSpeechInput';
import MergeClassNames from 'utils/MergeClassNames';

export interface VoiceMicButtonProps {
  isSupported: boolean;
  isRecording: boolean;
  lang: SpeechLang;
  langOptions: readonly SpeechLangOption[];
  onToggle: () => void;
  onLangChange: (lang: SpeechLang) => void;
  disabled?: boolean;
  className?: string;
}

const UNSUPPORTED_TOOLTIP = (
  <>
    Speech input is not supported in this browser. Use Chrome (v25+), Edge
    (v79+), or Safari (v14.1+) for voice input.
  </>
);

/**
 * Microphone button for voice input in text fields.
 *
 * - Left-click: toggle recording on/off.
 * - Right-click: open a 3-column language picker. The Tooltip is controlled
 *   so it is force-closed the moment the context menu opens.
 * - Checkmark icon marks the currently active language in the menu.
 * - Grayed and non-interactive when the browser does not support the Web
 *   Speech API, or when the parent field is disabled.
 * - Pulsing red ring while recording.
 * - Uses onPointerDown preventDefault to keep textarea focus (and therefore
 *   cursor position) intact when the button is clicked.
 */
const VoiceMicButton: React.FC<VoiceMicButtonProps> = ({
  isSupported,
  isRecording,
  lang,
  langOptions,
  onToggle,
  onLangChange,
  disabled = false,
  className,
}) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const isEffectivelyDisabled = !isSupported || disabled;
  const currentLangLabel =
    langOptions.find((o) => o.value === lang)?.label ?? lang;

  const tooltipContent = isSupported ? (
    <div className="flex flex-col gap-1">
      <span className="font-semibold text-white">
        {isRecording
          ? 'Recording — click to stop'
          : `Press to dictate: ${currentLangLabel}`}
      </span>
      <span className="text-gray-bright">Right-click to change language</span>
    </div>
  ) : (
    UNSUPPORTED_TOOLTIP
  );

  const button = (
    // Tooltip is controlled: onMouseEnter/Leave drive visibility, and the
    // Dropdown's onOpenChange forces it closed when the context menu opens.
    <Tooltip title={tooltipContent} placement="top" open={tooltipOpen}>
      {/* span wrapper required: Tooltip needs a DOM element that accepts ref */}
      <span
        className={MergeClassNames('inline-flex', className)}
        onMouseEnter={() => setTooltipOpen(true)}
        onMouseLeave={() => setTooltipOpen(false)}
      >
        <button
          type="button"
          disabled={isEffectivelyDisabled}
          aria-label={isRecording ? 'Stop voice input' : 'Start voice input'}
          className={MergeClassNames(
            'relative flex items-center justify-center rounded p-0.5 transition-colors focus:outline-none',
            isEffectivelyDisabled
              ? 'cursor-not-allowed text-gray-muted opacity-50'
              : isRecording
                ? 'cursor-pointer text-danger'
                : 'cursor-pointer text-gray-dark hover:text-charcoal'
          )}
          onClick={isEffectivelyDisabled ? undefined : onToggle}
          // Prevent the textarea from blurring (which would commit the value
          // and discard selectionStart/End) when the mic button is clicked.
          onPointerDown={(e) => e.preventDefault()}
        >
          <IoMdMic size={15} />
          {isRecording && (
            <span className="absolute inset-0 animate-ping rounded-full bg-danger opacity-25" />
          )}
        </button>
      </span>
    </Tooltip>
  );

  if (isEffectivelyDisabled) {
    return button;
  }

  return (
    <>
      {isRecording && (
        <VoiceOverlay lang={lang} langOptions={langOptions} onStop={onToggle} />
      )}
      <Dropdown
        open={dropdownOpen}
        trigger={['contextMenu']}
        placement="topLeft"
        onOpenChange={(open) => {
          setDropdownOpen(open);
          if (open) setTooltipOpen(false);
        }}
        dropdownRender={() => (
          <div className="rounded-lg bg-white py-1 shadow-lg">
            {/* grid-flow-col: fills column-by-column so items read top→bottom
              within each column. grid-rows-6 = ceil(17 langs / 3 cols). */}
            <div className="grid grid-cols-3 grid-rows-6 grid-flow-col">
              {langOptions.map((opt) => {
                const isSelected = lang === opt.value;
                const isDefault = opt.value === 'en-AU';
                return (
                  <button
                    key={opt.value}
                    type="button"
                    // All items carry border-l-[0.1875rem] so content never
                    // shifts when the border colour changes on selection.
                    // Selected: bright-gray fill + success left bar (mirrors the
                    // left tree's open-plan-node style).
                    // en-AU is always bold as it is the default language.
                    className={MergeClassNames(
                      'flex items-center gap-1.5 whitespace-nowrap border-l-[0.1875rem] px-3 py-1.5 text-sm text-charcoal',
                      isSelected
                        ? 'border-success bg-gray-bright'
                        : 'border-transparent hover:bg-gray-smoke',
                      (isSelected || isDefault) && 'font-semibold'
                    )}
                    // Preserve textarea focus so the user can keep typing after
                    // switching language without clicking the textarea again.
                    onPointerDown={(e) => e.preventDefault()}
                    onClick={() => {
                      onLangChange(opt.value as SpeechLang);
                      setDropdownOpen(false);
                    }}
                  >
                    {isSelected ? (
                      <IoMdCheckmark
                        size={13}
                        className="shrink-0 text-success"
                      />
                    ) : (
                      <span className="inline-block w-[0.8125rem] shrink-0" />
                    )}
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      >
        {button}
      </Dropdown>
    </>
  );
};

export default VoiceMicButton;
